import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import './InvoiceDetailPage.css';

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [inv, setInv]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [copied, setCopied]   = useState(false);
  const justPaid = searchParams.get('paid') === 'true';

  useEffect(() => {
    api.get(`/invoices/${id}`)
      .then(data => { setInv(data); setLoading(false); })
      .catch(() => navigate('/invoices'));
  }, [id, navigate]);

  async function handleSend() {
    setSending(true);
    setSendError(null);
    try {
      const res = await api.post(`/invoices/${id}/send`);
      setInv(i => ({ ...i, status: res.status, stripePaymentLinkId: res.paymentUrl }));
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  }

  function copyLink() {
    const url = inv?.stripePaymentLinkId;
    if (!url) return;
    if (navigator.clipboard && document.hasFocus()) {
      navigator.clipboard.writeText(url).then(() => flashCopied());
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      flashCopied();
    }
  }

  function flashCopied() {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) return <div className="inv-page"><div className="loading">Loading…</div></div>;

  const statusClass = inv.status.toLowerCase();

  return (
    <div className="inv-page">
      <div className="inv-header">
        <button className="back-btn" onClick={() => navigate('/invoices')}>← Invoices</button>
        <div className="inv-title-row">
          <h2>{inv.description}</h2>
          <span className={`badge badge-${statusClass}`}>{inv.status}</span>
        </div>
        <p className="inv-client">
          {inv.client.name}{inv.client.company ? ` · ${inv.client.company}` : ''}
          <span className="inv-client-email"> — {inv.client.email}</span>
        </p>
      </div>

      {justPaid && inv.status !== 'Paid' && (
        <div className="success-banner">Payment received — refreshing status shortly.</div>
      )}
      {justPaid && inv.status === 'Paid' && (
        <div className="success-banner">Payment confirmed!</div>
      )}

      <div className="inv-body">
        <div className="detail-grid">
          <div className="detail-card">
            <div className="detail-label">Amount Due</div>
            <div className="detail-value amount">${Number(inv.amountDue).toFixed(2)}</div>
          </div>
          <div className="detail-card">
            <div className="detail-label">Due Date</div>
            <div className="detail-value">{new Date(inv.dueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
          </div>
          <div className="detail-card">
            <div className="detail-label">Created</div>
            <div className="detail-value">{new Date(inv.createdAt).toLocaleDateString()}</div>
          </div>
          {inv.paidAt && (
            <div className="detail-card">
              <div className="detail-label">Paid</div>
              <div className="detail-value paid">{new Date(inv.paidAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
            </div>
          )}
        </div>

        {inv.status !== 'Paid' && (
          <div className="inv-actions">
            {inv.stripePaymentLinkId ? (
              <div className="payment-link-row">
                <div className="payment-link-label">Payment link</div>
                <div className="payment-link-box">
                  <span className="payment-link-url" title={inv.stripePaymentLinkId}>
                    {inv.stripePaymentLinkId.replace(/^https?:\/\//, '')}
                  </span>
                  <button className="btn-copy" onClick={copyLink}>{copied ? 'Copied!' : 'Copy'}</button>
                  <a className="btn-open" href={inv.stripePaymentLinkId} target="_blank" rel="noreferrer">Open ↗</a>
                </div>
                <button className="btn-primary resend-btn" onClick={handleSend} disabled={sending}>
                  {sending ? 'Resending…' : 'Resend Invoice Email'}
                </button>
              </div>
            ) : (
              <button className="btn-primary send-btn" onClick={handleSend} disabled={sending}>
                {sending ? 'Creating payment link…' : 'Send Invoice to Client'}
              </button>
            )}
            {sendError && <div className="send-error">{sendError}</div>}
          </div>
        )}

        {inv.status === 'Paid' && (
          <div className="paid-notice">
            <span className="paid-check">✓</span>
            Payment received on {new Date(inv.paidAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
          </div>
        )}
      </div>
    </div>
  );
}
